/**
 * @author DjordjeV
 */
import { mapTools } from '@pages/map/map'
import { transform as olTransform } from 'ol/proj'
import { saveAs } from 'file-saver'
import { SimpleGeometry } from 'ol/geom'
import earcut from 'earcut'
import proj4 from 'proj4'

export const XmlExport = {
    exportAreas: () => {
        const coordsNE: Coordinate[][][] = mapTools.areaTool.drawLayer
            .getSource()
            .getFeatures()
            .map((f) => (f.getGeometry() as SimpleGeometry).getCoordinates())

        const drawings = coordsNE.map((coord) =>
            coord.map((outline) => {
                const latLon = outline
                    .map((point) =>
                        olTransform(point, 'EPSG:3857', 'EPSG:4326')
                    )
                    .slice(0, outline.length - 1)
                const coords = latLon.map((p) => {
                    const c = proj4(kuura.getProjectProj4Text()).forward({
                        x: p[0],
                        y: p[1],
                        z: 0,
                    })
                    return [c.y, c.x]
                })
                const allPolys = []
                const polys = earcut(coords.flatMap((p) => p))
                for (let i = 0, len = polys.length; i < len; i += 3) {
                    allPolys.push([polys[i], polys[i + 1], polys[i + 2]])
                }
                return { coords, allPolys }
            })
        )

        const xml = `<?xml version="1.0" encoding="UTF-8"?>
<LandXML xmlns="http://www.landxml.org/schema/LandXML-1.2" date="${moment().format(
            'YYYY-MM-DD'
        )}" time="${moment().format('H:mm:ss')}">
\t<Units>
\t\t<Metric areaUnit="squareMeter" linearUnit="meter" volumeUnit="cubicMeter" temperatureUnit="celsius" pressureUnit="HPA"></Metric>
\t</Units>
\t<Application name="Infrakit" manufacturer="Infrakit Group Oy" manufacturerURL="https://www.infrakit.com/"></Application>
\t<Parcels>
\t\t${drawings
            .map(
                (drawing, i) =>
                    `<Parcel name="Area ${i + 1}">
\t\t\t<CoordGeom>
\t\t\t\t<IrregularLine>
\t\t\t\t\t<Start>${drawing[0].coords[0].join(' ')}</Start>
\t\t\t\t\t<End>${drawing[0].coords[0].join(' ')}</End>
\t\t\t\t\t<PntList2D>
\t\t\t\t\t\t${drawing.map((draw) => {
                        if (draw.coords.length > 0) {
                            return [...draw.coords]
                                .map((pair) => `${pair.join(' ')}`)
                                .join(' ')
                        }
                        return ''
                    })}
\t\t\t\t\t</PntList2D>
\t\t\t\t\t<PntList3D>
\t\t\t\t\t\t${drawing.map((coord) => {
                        if (coord.coords.length > 0) {
                            return [...coord.coords]
                                .map((pair) => `${pair.join(' ')} 0`)
                                .join(' ')
                        }
                        return ''
                    })}
\t\t\t\t\t</PntList3D>
\t\t\t\t</IrregularLine>
\t\t\t</CoordGeom>
\t\t</Parcel>`
            )
            .join('\n\t\t')}
\t</Parcels>
</LandXML>`

        const file = new Blob([xml], { type: 'text/xml;charset=utf-8' })
        saveAs(file, 'infrakit_area_drawings.xml')
    },
    exportDistances: () => {
        const lineCoordsNE = mapTools.distanceTool.drawLayer
            .getSource()
            .getFeatures()
            .map((f) => (f.getGeometry() as SimpleGeometry).getCoordinates())
        
        const lines = lineCoordsNE.map((line) => {
            return line.map((point) => {
                const latLon = olTransform(point, 'EPSG:3857', 'EPSG:4326')
                const c = proj4(kuura.getProjectProj4Text()).forward({
                    x: latLon[0],
                    y: latLon[1],
                    z: 0,
                })
                return [c.y, c.x]
            })
        })
        
        const currentDate = moment().format('YYYY-MM-DD')
        const currentTime = moment().format('HH:mm:ss')

        const xml = `<?xml version="1.0" encoding="UTF-8"?>
<LandXML xmlns="http://www.landxml.org/schema/LandXML-1.2" date="${currentDate}" time="${currentTime}">
\t<Units>
\t\t<Metric areaUnit="squareMeter" linearUnit="meter" volumeUnit="cubicMeter" temperatureUnit="celsius" pressureUnit="HPA"/>
\t</Units>
\t<Surfaces name="Distance_Surfaces">
\t\t<Surface name="Distance_Surface">
\t\t\t<SourceData>
\t\t\t\t<Breaklines>
${lines
    .map((line, i) => {
      if (line.length < 2) return ''; // Skip lines with insufficient points
      // Create 3D points list with default elevation of 0
      const points3D = line.map(point => `${point[0]} ${point[1]} 0.000000`).join(' ');
      return `\t\t\t\t\t<Breakline brkType="standard" name="${i + 1}">
  \t\t\t\t\t\t<PntList3D>${points3D}</PntList3D>
  \t\t\t\t\t\t<Feature name="IM_${i + 2}" code="IM_coding" source="inframodel">
  \t\t\t\t\t\t\t<Property label="infraCoding" value="2121"/>
  \t\t\t\t\t\t\t<Property label="infraCodingDesc" value="Distance Line ${i + 1}"/>
  \t\t\t\t\t\t</Feature>
  \t\t\t\t\t</Breakline>`;
    })
    .filter(line => line !== '')
    .join('\n')}
\t\t\t\t</Breaklines>
\t\t\t</SourceData>
\t\t</Surface>
\t</Surfaces>
</LandXML>`

        const file = new Blob([xml], { type: 'text/xml;charset=utf-8' })
        saveAs(file, 'infrakit_distance_drawings.xml')
    },
    exportSurface: () => {
        const coordsNE = mapTools.areaTool.drawLayer
            .getSource()
            .getFeatures()
            .map((f) => (f.getGeometry() as SimpleGeometry).getCoordinates())

        const surfaces = coordsNE.map((coord, surfaceIndex) => {
            const surfaceName = `Surface ${surfaceIndex + 1}`

            let allPoints = []
            const allFaces = []

            coord.forEach((outline) => {
                const latLon = outline
                    .map((point) =>
                        olTransform(point, 'EPSG:3857', 'EPSG:4326')
                    )
                    .slice(0, outline.length - 1)

                const coords = latLon.map((p) => {
                    const c = proj4(kuura.getProjectProj4Text()).forward({
                        x: p[0],
                        y: p[1],
                        z: 0,
                    })
                    return [c.y, c.x, 0]
                })

                const pointOffset = allPoints.length
                allPoints = [...allPoints, ...coords]

                const flatCoords = coords.flatMap((p) => [p[0], p[1]])
                const triangles = earcut(flatCoords)

                for (let i = 0; i < triangles.length; i += 3) {
                    const p1 = triangles[i] + pointOffset + 1
                    const p2 = triangles[i + 1] + pointOffset + 1
                    const p3 = triangles[i + 2] + pointOffset + 1

                    allFaces.push([p1, p2, p3])
                }
            })

            return {
                name: surfaceName,
                points: allPoints,
                faces: allFaces,
            }
        })

        const currentDate = moment().format('YYYY-MM-DD')
        const currentTime = moment().format('HH:mm:ss')

        const xml = `<?xml version="1.0" encoding="UTF-8"?>
<LandXML xmlns="http://www.landxml.org/schema/LandXML-1.2" date="${currentDate}" time="${currentTime}">
\t<Units>
\t\t<Metric areaUnit="squareMeter" linearUnit="meter" volumeUnit="cubicMeter" temperatureUnit="celsius" pressureUnit="HPA"/>
\t</Units>
\t<Application name="Infrakit" manufacturer="Infrakit Group Oy" manufacturerURL="https://www.infrakit.com">
\t</Application>
\t<Surfaces>
\t\t${surfaces
            .map(
                (surface) =>
                    `\t\t<Surface name="${surface.name}">
\t\t\t<Definition surfType="TIN">
\t\t\t\t<Pnts>
${surface.points
    .map((point, i) => `\t\t\t\t\t<P id="${i + 1}">${point.join(' ')}</P>`)
    .join('\n')}
\t\t\t\t</Pnts>
\t\t\t\t<Faces>
${surface.faces.map((face) => `\t\t\t\t\t<F>${face.join(' ')}</F>`).join('\n')}
\t\t\t\t</Faces>
\t\t\t</Definition>
\t\t</Surface>`
            )
            .join('\n\t\t')}
\t</Surfaces>
</LandXML>`

        const file = new Blob([xml], { type: 'text/xml;charset=utf-8' })
        saveAs(file, 'infrakit_surface_tin.xml')
    },
}
